// imports
import { CircularProgress } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// interfaces, constants, helper
import { SECONDARY_FONT } from 'constants/fonts';
import { Nullable } from 'interfaces';
import { CountdownTimerProps } from 'interfaces/PropsTypes';
import { CircularProgressContainer, TimeDisplay, TimerContainer } from 'theme/styleComponent';

export default function CountdownTimer({
  initialTime = 10,
  onComplete,
  size = 200,
  color = 'error',
  thickness = 2,
  startTimer = false,
}: CountdownTimerProps) {
  const [timeLeft, setTimeLeft] = useState(initialTime);
  const [isActive, setIsActive] = useState(true);
  const intervalRef = useRef<Nullable<NodeJS.Timeout>>(null);
  const hasCompletedRef = useRef(false);

  const progress = useMemo(() => {
    return 100 - (timeLeft / initialTime) * 100;
  }, [timeLeft, initialTime]);

  const handleTimerComplete = useCallback(() => {
    if (hasCompletedRef.current) return;

    hasCompletedRef.current = true;
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setIsActive(false);
    onComplete();
  }, [onComplete]);

  useEffect(() => {
    if (isActive && timeLeft > 0 && startTimer) {
      intervalRef.current = setInterval(() => {
        setTimeLeft((prevTime) => {
          const nextTime = prevTime - 1;
          if (!nextTime) {
            handleTimerComplete();
          }
          return nextTime;
        });
      }, 1000);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, timeLeft, startTimer]);

  const timerColor = timeLeft < 4 ? 'error.dark' : 'primary.main';

  if (!startTimer) return <></>;

  return (
    <TimerContainer>
      <CircularProgressContainer>
        <CircularProgress
          variant="determinate"
          value={progress}
          size={size * 1.05}
          thickness={thickness * 1.2}
          color={color}
          sx={{
            color: (theme) => theme.palette.error.main,
            zIndex: 10,
            borderRadius: '50%',
            '& .MuiCircularProgress-circle': {
              strokeLinecap: 'round',
            },
          }}
        />
        <CircularProgress
          variant="determinate"
          value={100}
          size={size}
          thickness={thickness / 4}
          sx={{
            position: 'absolute',
            left: 5,
            transform: 'rotate(-90deg)',
            color: (theme) => theme.palette.primary.main,
          }}
        />
        <TimeDisplay
          variant="h1"
          fontFamily={SECONDARY_FONT}
          sx={{
            color: timerColor,
          }}>
          {timeLeft}
        </TimeDisplay>
      </CircularProgressContainer>
    </TimerContainer>
  );
}
